import uniqBy from "lodash/uniqBy";
import isEqual from "lodash/isEqual";

import Api from "apps/discovery/util/web";
import { SECTIONS, STATUSES } from "apps/discovery/util/consts";
import { fetchBinaryQuestion } from "./questions";


// initial state
// ----------------------------------------------------------------------------

export const INITIAL_STATE = {
	isMounted: false,
	isLoaded: false,
	sections: {},
	sectionOrder: [],
	questionMatchResults: {},
	questionResults: [],
	questions: {},
	isGettingQuestionMatches: false,
	statuses: Object.values(SECTIONS).reduce((statuses, section) => ({
		...statuses,
		[section]: STATUSES.loading,
	}), {}),
};

const getInitialState = () => {
	return INITIAL_STATE;
};


// action types
// ----------------------------------------------------------------------------

export const ON_MOUNT = "DISCOVERY.ON_MOUNT";

export const GET_SECTIONS = "DISCOVERY.GET_SECTIONS";
export const GET_SECTIONS_SUCCESS = "DISCOVERY.GET_SECTIONS_SUCCESS";
export const GET_SECTIONS_FAILURE = "DISCOVERY.GET_SECTIONS_FAILURE";

export const GET_SECTION = "DISCOVERY.GET_SECTION";
export const GET_SECTION_SUCCESS = "DISCOVERY.GET_SECTION_SUCCESS";
export const GET_SECTION_ERROR = "DISCOVERY.GET_SECTION_ERROR";

export const SEARCH_INTEREST = "DISCOVERY.SEARCH_INTEREST";
export const SEARCH_INTEREST_SUCCESS = "DISCOVERY.SEARCH_INTEREST_SUCCESS";
export const SEARCH_INTEREST_ERROR = "DISCOVERY.SEARCH_INTEREST_ERROR";

export const SEARCH_QUESTION = "DISCOVERY.SEARCH_QUESTION";
export const SEARCH_QUESTION_SUCCESS = "DISCOVERY.SEARCH_QUESTION_SUCCESS";
export const SEARCH_QUESTION_ERROR = "DISCOVERY.SEARCH_QUESTION_ERROR";

export const GET_INTEREST_MATCHES = "DISCOVERY.GET_INTEREST_MATCHES";
export const GET_INTEREST_MATCHES_SUCCESS = "DISCOVERY.GET_INTEREST_MATCHES_SUCCESS";
export const GET_INTEREST_MATCHES_ERROR = "DISCOVERY.GET_INTEREST_MATCHES_ERROR";

export const GET_QUESTION_MATCHES = "DISCOVERY.GET_QUESTION_MATCHES";
export const GET_QUESTION_MATCHES_SUCCESS = "DISCOVERY.GET_QUESTION_MATCHES_SUCCESS";
export const GET_QUESTION_MATCHES_ERROR = "DISCOVERY.GET_QUESTION_MATCHES_ERROR";

export const CHANGE_PAGE = "DISCOVERY.CHANGE_PAGE";
export const CHANGE_IMPORTANT_QUESTION = "DISCOVERY.CHANGE_IMPORTANT_QUESTION";
export const INSERT_IMPORTANT_QUESTION = "DISCOVERY.INSERT_IMPORTANT_QUESTION";

export const GET_QUESTION = "DISCOVERY.GET_QUESTION";
export const GET_QUESTION_SUCCESS = "DISCOVERY.GET_QUESTION_SUCCESS";
export const GET_QUESTION_ERROR = "DISCOVERY.GET_QUESTION_ERROR";

// reducer
// ----------------------------------------------------------------------------

export function reducer(state = getInitialState(), action) {
	switch (action.type) {
		case ON_MOUNT: {
			return {
				...state,
				isMounted: true,
			};
		}

		case GET_SECTIONS: {
			return {
				...state,
				isLoaded: false,
			};
		}

		case GET_SECTIONS_SUCCESS: {
			return {
				...state,
				sections: action.payload.reduce((sections, section) => ({
					...sections,
					[section.section_id]: section,
				}), {}),
				sectionOrder: action.payload.map(({ section_id: sectionId }) => sectionId),
				isLoaded: true,
			};
		}

		case GET_SECTIONS_FAILURE: {
			return {
				...state,
				isLoaded: true,
				error: action.payload.error,
			};
		}

		case GET_SECTION: {
			const { sectionId } = action.payload;
			return {
				...state,
				statuses: {
					...state.statuses,
					[sectionId]: STATUSES.loading,
				},
			};
		}

		case GET_SECTION_SUCCESS: {
			const { section: newSection, sectionId } = action.payload;
			const newState = {
				...state,
				sections: {
					...state.sections,
					[action.payload.section.section_id]: newSection,
				},
				statuses: {
					...state.statuses,
					[sectionId]: STATUSES.success,
				},
			};

			// if we are fetching an important question section, add its results to
			// the important question section cache
			if (
				newSection.section_id === SECTIONS.important_questions &&
				newSection.results.rows && newSection.results.rows.length
			) {
				newState.questionMatchResults = {
					...newState.questionMatchResults,
					[newSection.results.rows[0].id]: newSection.results.rows[0],
				};
			}

			return newState;
		}

		case GET_SECTION_ERROR: {
			const { sectionId } = action.payload;

			return {
				...state,
				sections: {
					...state.sections,
					[sectionId]: {
						...state.sections[sectionId],
						error: true,
					},
				},
				statuses: {
					...state.statuses,
					[sectionId]: STATUSES.error,
				},
			};
		}

		case SEARCH_INTEREST: {
			return {
				...state,
				isSearchingInterest: true,
				interestResults: [],
			};
		}

		case SEARCH_INTEREST_SUCCESS: {
			let results = [];

			if (action.payload.interests && action.payload.interests.data) {
				results = action.payload.interests.data;

				results = results.map((result) => {
					return {
						text: result.name,
						subtext: null,
						id: result.id,
					};
				});
			}

			return {
				...state,
				isSearchingInterest: false,
				interestResults: results,
			};
		}

		case SEARCH_INTEREST_ERROR: {
			return {
				...state,
				isSearchingInterest: false,
				interestResults: [],
			};
		}

		case GET_INTEREST_MATCHES: {
			return {
				...state,
				isGettingInterestMatches: true,
			};
		}

		case GET_INTEREST_MATCHES_SUCCESS: {
			// the payload of this action is row data, along with `id`, which is the
			// id of the interest that was searched. here, we decide what to do with
			// the returned data; if there aren't any user interest searches, we can
			// add this, but if there are more than the max we'll kill the last one.
			if (!state.sections[SECTIONS.interests]) {
				return { ...state };
			}

			const rows = [action.payload.row];

			return {
				...state,
				isGettingInterestMatches: false,
				sections: {
					...state.sections,
					[SECTIONS.interests]: {
						...state.sections[SECTIONS.interests],
						results: {
							...state.sections[SECTIONS.interests].results,
							rows,
						},
					},
				},
			};
		}

		case GET_INTEREST_MATCHES_ERROR: {
			return {
				...state,
				isGettingInterestMatches: false,
			};
		}

		case SEARCH_QUESTION: {
			return {
				...state,
				isSearchingQuestion: true,
			};
		}

		case SEARCH_QUESTION_SUCCESS: {
			const questions = action.payload.results;

			// Format results.
			const questionResults = questions.map((question) => ({
				question,
				text: question.question_text,
			}));

			return {
				...state,
				isSearchingQuestion: false,
				questionResults,
			};
		}

		case SEARCH_QUESTION_ERROR: {
			return {
				...state,
				isSearchingQuestion: false,
				questionResults: [],
			};
		}

		case GET_QUESTION: {
			return {
				...state,
				isLoadingQuestion: true,
			};
		}

		case GET_QUESTION_SUCCESS: {
			return {
				...state,
				questions: {
					...state.questions,
					[action.payload.question.id]: {
						...action.payload.question,
						category: action.payload.question.genre,
					},
				},
				isLoadingQuestion: false,
			};
		}

		case GET_QUESTION_ERROR: {
			return {
				...state,
				isLoadingQuestion: false,
			};
		}

		case GET_QUESTION_MATCHES: {
			return {
				...state,
				isGettingQuestionMatches: true,
			};
		}

		case GET_QUESTION_MATCHES_SUCCESS: {
			if (!state.sections[SECTIONS.important_questions]) {
				return { ...state };
			}

			return {
				...state,
				isGettingQuestionMatches: false,
				sections: {
					...state.sections,
					[SECTIONS.important_questions]: {
						...state.sections[SECTIONS.important_questions],
						results: {
							...state.sections[SECTIONS.important_questions].results,
							rows: [action.payload.row],
						},
					},
				},
				questionMatchResults: {
					...state.questionMatchResults,
					[action.payload.questionId]: {
						...action.payload.row,
						answers: action.payload.selectedAnswers,
					},
				},
			};
		}

		case GET_QUESTION_MATCHES_ERROR: {
			return {
				...state,
				isGettingQuestionMatches: false,
			};
		}

		case CHANGE_PAGE: {
			const { sectionId, page, rowId } = action.payload;

			const results = {
				...state.sections[sectionId].results,
			};

			if (results.rows.length === 1) {
				results.rows[0].currentPage = page;
			} else {
				results.rows.forEach((row, i) => {
					if (row.id === rowId) {
						results.rows[i] = {
							...row,
							currentPage: page,
						};
					}
				});
			}

			const newState = {
				...state,
				sections: {
					...state.sections,
					[sectionId]: {
						...state.sections[sectionId],
						results: {
							...state.sections[sectionId].results,
							...results,
						},
					},
				},
			};

			return newState;
		}

		case CHANGE_IMPORTANT_QUESTION: {
			return {
				...state,
				sections: {
					...state.sections,
					[SECTIONS.important_questions]: {
						...state.sections[SECTIONS.important_questions],
						header_component: {
							...state.sections[SECTIONS.important_questions].header_component,
							currentPage: action.payload.page,
						},
					},
				},
			};
		}

		case INSERT_IMPORTANT_QUESTION: {
			const section = state.sections[SECTIONS.important_questions];

			return {
				...state,
				sections: {
					...state.sections,
					[SECTIONS.important_questions]: {
						...section,
						header_component: {
							...section.header_component,
							metadata: {
								...section.header_component.metadata,
								questions: uniqBy([
									action.payload.question,
									...section.header_component.metadata.questions,
								], "id").slice(0, 5),
							},
							currentPage: 0,
						},
					},
				},
			};
		}

		default: {
			return state;
		}
	}
}


// actions
// ----------------------------------------------------------------------------

export const getSections = () => (dispatch) => {
	dispatch({
		type: GET_SECTIONS,
	});

	return Api.getSections()
		.then((sections) => {
			dispatch({
				type: GET_SECTIONS_SUCCESS,
				payload: sections,
			});
		})
		.catch((error) => {
			dispatch({
				type: GET_SECTIONS_FAILURE,
				payload: {
					error,
				},
			});
		});
};

export const mount = () => {
	return (dispatch) => {
		dispatch({ type: ON_MOUNT });

		// Reload page data.
		dispatch(getSections());
		dispatch(fetchBinaryQuestion());
	};
};

export const getSection = (sectionId) => {
	return (dispatch) => {
		dispatch({
			type: GET_SECTION,
			payload: {
				sectionId,
			},
		});

		Api.getSection(sectionId)
			.then((section) => {
				dispatch({
					type: GET_SECTION_SUCCESS,
					payload: {
						sectionId,
						section,
					},
				});
			})
			.catch((error) => {
				dispatch({
					type: GET_SECTION_ERROR,
					payload: {
						sectionId,
						error,
					},
				});
			});
	};
};

export const searchInterest = (query) => {
	return (dispatch) => {
		dispatch({ type: SEARCH_INTEREST });

		Api.searchInterest(query)
			.then((interests) => {
				dispatch({
					type: SEARCH_INTEREST_SUCCESS,
					payload: {
						query,
						interests,
					},
				});
			})
			.catch((error) => {
				dispatch({
					type: SEARCH_INTEREST_ERROR,
					payload: {
						error,
					},
				});
			});
	};
};

export const getInterestMatches = (params) => {
	return (dispatch) => {
		dispatch({ type: GET_INTEREST_MATCHES });

		Api.getInterestMatches(params)
			.then((res) => {
				dispatch({
					type: GET_INTEREST_MATCHES_SUCCESS,
					payload: {
						row: res,
					},
				});
			})
			.catch((error) => {
				dispatch({
					type: GET_INTEREST_MATCHES_ERROR,
					payload: {
						error,
					},
				});
			});
	};
};

export const searchQuestion = (query) => {
	return (dispatch) => {
		// Don't bother running a search here.
		if (!query) {
			return dispatch({
				type: SEARCH_QUESTION_SUCCESS,
				payload: {
					query,
					results: [],
				},
			});
		}

		dispatch({ type: SEARCH_QUESTION });

		return Api.searchQuestion(query)
			.then((results) => {
				dispatch({
					type: SEARCH_QUESTION_SUCCESS,
					payload: {
						query,
						results,
					},
				});
			})
			.catch((error) => {
				dispatch({
					type: SEARCH_QUESTION_ERROR,
					payload: {
						error,
					},
				});
			});
	};
};

export const getQuestion = (query) => {
	return (dispatch) => {
		dispatch({ type: GET_QUESTION });

		return Api.getQuestion(query)
			.then((question) => {
				dispatch({
					type: GET_QUESTION_SUCCESS,
					payload: {
						question,
					},
				});
			})
			.catch((error) => {
				dispatch({
					type: GET_QUESTION_ERROR,
					payload: {
						error,
					},
				});
			});
	};
};

export const getQuestionMatches = (questionId, selectedAnswers) => {
	return (dispatch, getStore) => {
		dispatch({ type: GET_QUESTION_MATCHES });

		const store = getStore().discovery;

		// if this question exists in the store, return the cached version instead
		// of hitting the api
		const cachedResults = store.questionMatchResults && store.questionMatchResults[questionId];
		const isCached = cachedResults && isEqual(cachedResults.answers, selectedAnswers);

		if (isCached) {
			dispatch({
				type: GET_QUESTION_MATCHES_SUCCESS,
				payload: {
					row: cachedResults,
					questionId,
				},
			});

			return;
		}

		Api.getQuestionMatches(questionId, selectedAnswers)
			.then((res) => {
				dispatch({
					type: GET_QUESTION_MATCHES_SUCCESS,
					payload: {
						row: res,
						questionId,
						selectedAnswers,
					},
				});
			})
			.catch((error) => {
				dispatch({
					type: GET_QUESTION_MATCHES_ERROR,
					payload: {
						error,
					},
				});
			});
	};
};

export const runQuestionSearch = (questionId, selectedAnswers) => {
	return (dispatch, getState) => {
		const question = getState().discovery.questions[questionId];

		dispatch({
			type: INSERT_IMPORTANT_QUESTION,
			payload: {
				question: {
					id: question.id,
					category: question.genre,
					text: question.text,
					answer: [...selectedAnswers]
						.sort()
						.map((answerIndex) => question.answers[answerIndex])
						.join(", "),
				},
			},
		});
		dispatch(getQuestionMatches(questionId, selectedAnswers));
	};
};

export const changePage = (payload) => {
	return {
		type: CHANGE_PAGE,
		payload,
	};
};

export const changeImportantQuestion = (page) => {
	return {
		type: CHANGE_IMPORTANT_QUESTION,
		payload: {
			page,
		},
	};
};



// WEBPACK FOOTER //
// ./src/ducks/discovery.js